import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { AbsencePlannerEventCSSClass } from '../utils/absence-planner.constants';
import { AbsencePlannerEventDragResizeService } from './services/absence-planner-event-drag-resize.service';
import { DateTimeHelper } from '../../calendar/util/date-time-helper';
import { AbsencePlannerEventModel } from '../../../core/models/absence-planner/absence-planner-event.model';
import { EventResizeHandleComponent } from './parts/event-drag-handle/event-resize-handle.component';
import { EventResizeHandleAlignment } from './parts/event-drag-handle/event-resize-handle-alignment';
import { IFixIntervalViewType } from '../../calendar/components/time-block/types/time-block-types';
import { AbsencePlannerService } from '../services/absence-planner.service';
import { assert } from '../../../core/assert/assert';
import { SubSink } from 'subsink';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-planner-event',
  templateUrl: './absence-planner-event.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [AbsencePlannerEventDragResizeService],
})
export class AbsencePlannerEventComponent implements AfterViewInit, OnDestroy {
  eventWrapper = viewChild<ElementRef<HTMLDivElement>>('eventWrapper');
  leftDragEdge = viewChild<EventResizeHandleComponent>('leftDragEdge');
  rightDragEdge = viewChild<EventResizeHandleComponent>('rightDragEdge');
  plannerEventModel = input.required<AbsencePlannerEventModel>();
  eventWidth = input.required<number>();
  slotWidth = input.required<number>();
  eventContainer = input.required<HTMLDivElement>();
  eventLeft = input<number>(0);
  eventTop = signal<number>(0);
  maxParallelIndexPerGroup = signal<number>(0);
  public readonly absencePlannerEventCSSClass = AbsencePlannerEventCSSClass;
  public readonly DateTimeHelper = DateTimeHelper;
  public readonly EventDragHandleAlignment = EventResizeHandleAlignment;
  private readonly absencePlannerEventDragResizeService = inject(
    AbsencePlannerEventDragResizeService,
  );
  private readonly absencePlannerService = inject(AbsencePlannerService);
  private readonly subs = new SubSink();

  ngAfterViewInit(): void {
    // Init drag and resizing handling
    this.absencePlannerEventDragResizeService.initializeDragAndDrop(
      this.eventWrapper(),
      this.slotWidth(),
    );
    this.absencePlannerEventDragResizeService.initializeDragAndDrop(
      this.leftDragEdge().dragHandle(),
      this.slotWidth(),
    );
    this.absencePlannerEventDragResizeService.initializeDragAndDrop(
      this.rightDragEdge().dragHandle(),
      this.slotWidth(),
    );

    this.subs.sink = this.absencePlannerService.maxParallelIndexPerGroupChanged$
      .pipe(filter((maxParallelIndexPerGroup) => maxParallelIndexPerGroup.size > 0))
      .subscribe((maxParallelIndexPerGroup) => {
        // Set CSS coordinates
        const userId = this.plannerEventModel().userId;
        const maxParallelIndexForGroup = maxParallelIndexPerGroup.get(userId);

        assert(maxParallelIndexForGroup !== undefined);

        this.maxParallelIndexPerGroup.set(maxParallelIndexForGroup);
        const parallelIndex = (this.plannerEventModel().timeBlockViewType as IFixIntervalViewType)
          .parallelIndex;

        const top =
          maxParallelIndexForGroup === 0
            ? 0
            : parallelIndex * this.eventWrapper().nativeElement.getBoundingClientRect().height +
              (parallelIndex + 1) * 2;
        this.eventTop.set(top);
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
